import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { colorSurface, radiusMd } from "@porsche-design-system/components-react/emotion"
import type React from "react"
import type { PropsWithChildren } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"

type AspectRatioValue = "16:9" | "4:3" | "1:1" | AspectRatioValueSanitized
type AspectRatioValueSanitized = "16/9" | "4/3" | "1/1" | "auto" | number

export interface PictureProps {
	/**
	 * The alternative text of the image.
	 */
	alt?: string

	/**
	 * Sets the aspect ratio of the image.
	 * If **"auto"** is used, the source files determines the aspect ratio. You can also provide a custom aspect ratio.
	 */
	aspectRatio: AspectRatioValue

	/**
	 * You can customize the border radius of the image.
	 */
	borderRadius?: string
	className?: string

	/**
	 * The html5 draggable attribute
	 */
	draggable?: boolean

	/**
	 * Only loads the image when it is scrolled in the visible portion of the viewport.
	 */
	lazy?: boolean

	/**
	 * Callback when the picture is clicked.
	 */
	onClick?: () => void
	/**
	 * Callback when the picture finished downloading.
	 */
	onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void

	/**
	 * You can customize the placeholder color of the image with a hex code.
	 */
	placeholderColor?: string

	/**
	 * You can provide a very small (only a couple pixels wide) base64 encoded preview image string or image url that will be displayed
	 * with a blur filter while the proper image is loaded by the browser.
	 */
	placeholderImage?: string
}

export const Picture = (props: PropsWithChildren<PictureProps>) => {
	const {
		alt,
		aspectRatio,
		borderRadius,
		children,
		draggable,
		lazy,
		onClick,
		onLoad,
		placeholderColor,
		placeholderImage,
		...rest
	} = props

	const sanitizedAspectRatio = sanitizeAspectRatioValue(aspectRatio)
	return (
		<StyledPicture
			onClick={onClick}
			$aspectRatio={sanitizedAspectRatio}
			data-aspect-ratio={sanitizedAspectRatio}
			$backgroundColor={placeholderColor}
			$placeholderImage={placeholderImage}
			$borderRadius={borderRadius}
			{...rest}
		>
			{children}
			<StyledImage
				loading={lazy ? "lazy" : "eager"}
				alt={alt}
				onLoad={onLoad}
				draggable={draggable}
				$aspectRatio={sanitizedAspectRatio}
			/>
		</StyledPicture>
	)
}

const sanitizeAspectRatioValue = (sourceValue: AspectRatioValue) => {
	if (typeof sourceValue === "string") {
		return sourceValue.replace(/:/g, "/") as AspectRatioValueSanitized
	}
	return sourceValue as AspectRatioValueSanitized
}

const StyledPicture = styled("picture", { shouldForwardProp })<{
	$aspectRatio: AspectRatioValueSanitized
	$backgroundColor?: string
	$borderRadius?: string
	$placeholderImage?: string
}>`
	position: relative;
	overflow: hidden;
	aspect-ratio: ${(props) => props.$aspectRatio};
	width: 100%;
	display: block;
	cursor: ${(props) => (props.onClick ? "pointer" : undefined)};
	background: ${(props) => props.$backgroundColor ?? colorSurface};
	border-radius: ${({ $borderRadius }) => $borderRadius ?? radiusMd};
	
	${({ $placeholderImage }) =>
		$placeholderImage &&
		css`
	&::before {
		content: "";
		position: absolute;
		inset: 0;
		background-image: url('${$placeholderImage}');
		background-size: cover;
		filter: blur(20px);
		transform: scale(1.1, 1.1);
	}
		`}
`

const StyledImage = styled("img", { shouldForwardProp })<{ $aspectRatio: AspectRatioValueSanitized }>`
	vertical-align: top; /* fixes line-height bottom gap */
	width: 100%;
	/* safari can't handle height: 100% causing the image to overflow/scale beyond the picture which gets cropped with overflow: hidden */
	${({ $aspectRatio }) => ($aspectRatio !== "auto" ? `aspect-ratio: ${$aspectRatio};` : "height: 100%;")}
	object-fit: cover;
`
